import { computed, inject, isDevMode } from '@angular/core';
import {
  RemoteCommunication,
  RemoteState,
  type StandardPayload,
} from '@navstation-portal/iframe/util/remote-communication';
import { IframeWhitelistService } from '@navstation-portal/iframe/util/whitelist';
import { LoggerStore } from '@navstation-portal/shared/util/logger';
import { deepComputed, patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';

const initialState: RemoteState = {
  iframeId: null,
  state: 'disconnected',
  parentOrigin: null,
  headless: false,
  settings: {},
  messages: [],
  error: null,
};

export const RemoteStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed(({ state, parentOrigin, iframeId, headless, settings, messages, error }) => ({
    isConnected: computed(() => state() === 'connected'),
    isConnecting: computed(() => state() === 'connecting'),
    isFailed: computed(() => state() === 'failed'),
    isDisconnected: computed(() => state() === 'disconnected'),

    isHeadless: computed(() => headless()),
    theme: computed(() => settings()?.theme),
    language: computed(() => settings()?.language),
    timezone: computed(() => settings()?.timezone),

    lastMessage: computed(() => {
      const msgs = messages();
      return msgs[msgs.length - 1] ?? null;
    }),
    messageCount: computed(() => messages().length),

    hasError: computed(() => error() !== null),

    connectionInfo: deepComputed(() => ({
      state: state(),
      origin: parentOrigin(),
      iframeId: iframeId(),
      error: error(),
      isReady: state() === 'connected' && parentOrigin() !== null && iframeId() !== null,
    })),

    canSendMessages: computed(() => state() === 'connected' && parentOrigin() !== null && iframeId() !== null),
  })),

  withMethods((store) => {
    const whitelistService = inject(IframeWhitelistService);
    const logger = inject(LoggerStore).forFeature('Remote');

    const remote = new RemoteCommunication({
      whitelist: whitelistService.whitelist,
      isDev: isDevMode(),
    });

    remote.subscribe((remoteState) => {
      logger.log('State changed:', remoteState.state);
      patchState(store, remoteState);
    });

    return {
      send: (payload: StandardPayload) => remote.send(payload),
      _init: () => remote.init(),
      _destroy: () => remote.destroy(),
    };
  }),

  withHooks((store) => ({
    onInit() {
      store._init();
    },
    onDestroy() {
      store._destroy();
    },
  }))
);
